// ---------------------------------------------------------------------------
// spintaxPageDeriver.js
// Derives a unique page JSON from the spintax content template
// by resolving all {a|b|c} groups using a per-page SeededRNG.
// Now also accepts a pre-generated specialist SEO title.
// ---------------------------------------------------------------------------
import { resolveSpintax } from './spintaxEngine.js';
import { stripGeo } from './geoSanitiser.js';
import { naturaliseKeyword, stripCtaText } from './keywordReplacer.js';

const resolveStr = (val, rng) =>
  stripGeo(resolveSpintax(typeof val === 'string' ? val : '', rng));

const resolveArr = (arr, rng) =>
  Array.isArray(arr) ? arr.map((v) => resolveStr(v, rng)) : [];

// Cards carry no calls to action; the article builder places at most
// three, in commercial spots only.

const closeSentence = (text) => {
  const body = String(text || '').trim();
  return body && !/[.!?]$/.test(body) ? `${body}.` : body;
};

// Throat-clearing openers that delay the answer ("In this walkthrough...").
const FILLER_OPENING = /^(?:in this (?:walkthrough|video|review|article|guide|post|breakdown)|welcome|hello|hi there|are you (?:wondering|thinking|curious|looking)|if you(?:'re| are) (?:wondering|thinking|curious|looking)|have you (?:ever )?(?:wondered|heard)|today,? we|let's (?:take a look|dive|explore)|in today's)\b/i;

/**
 * The intro must open with the answer. If the first sentence is filler,
 * drop it; if nothing useful is left, open with the evaluation verdict.
 */
const answerFirstIntro = (intro, verdict) => {
  let text = String(intro || '').trim();
  if (FILLER_OPENING.test(text)) {
    const rest = text.replace(/^[^.!?]*[.!?]\s*/, '').trim();
    text = rest.length > 40 ? rest : '';
  }
  return text || verdict || intro;
};

const pickFaq = (faq, rng) => {
  const items = [...faq];
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng._next() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items.slice(0, Math.min(items.length, 3 + Math.floor(rng._next() * 3)));   // 3-5 per page
};

/**
 * Choose which 6 card topics this page shows, from a pool of up to 9.
 * The verdict (first) and final recommendation (last) always frame the
 * page; 4 of the middle topics are picked per page, kept in reading order.
 * With 9 topics that's 35 different combinations.
 */
const pickCardIndexes = (poolSize, rng) => {
  if (poolSize <= 6) return [0, 1, 2, 3, 4, 5];
  const middle = Array.from({ length: poolSize - 2 }, (_, i) => i + 1);
  for (let i = middle.length - 1; i > 0; i--) {
    const j = Math.floor(rng._next() * (i + 1));
    [middle[i], middle[j]] = [middle[j], middle[i]];
  }
  return [0, ...middle.slice(0, 4).sort((a, b) => a - b), poolSize - 1];
};

/**
 * Derive a fully resolved (no spintax) page JSON for a single page.
 *
 * @param {Object} spintaxContent - The raw spintax template from the LLM
 * @param {string} keyword
 * @param {string} product
 * @param {SeededRNG} rng - Per-page seeded RNG
 * @param {string} pageTitle - The user's SEO title + random code, used as-is
 * @returns {Object} - Resolved llmJson ready for applyTransformations()
 */
export const derivePageFromSpintax = (
  spintaxContent,
  keyword,
  product,
  rng,
  pageTitle
) => {
  const s = spintaxContent;

  // Body text never contains the long SEO-title phrase; it reads as the
  // plain product name instead. (Titles below use the phrase as-is.)
  // ...and never carries pushy sales CTAs.
  const natural = (text) => stripCtaText(naturaliseKeyword(text, keyword, product), product);
  const text = (val) => natural(resolveStr(val, rng));
  const list = (arr) => resolveArr(arr, rng).map(natural);

  const subtitle = text(s.subtitle);
  const trailing = text(s.trailing);

  // Video cards: 6 of the topic pool, varied per page. No CTA endings.
  const poolTitles = list(s.card_titles);
  const poolContents = list(s.card_contents);
  const poolSize = Math.min(poolTitles.length, poolContents.length);
  const picked = pickCardIndexes(poolSize, rng);
  const cards = {
    titles: picked.map((i) => poolTitles[i] || product),
    contents: picked.map((i) => closeSentence(poolContents[i])),
  };
  // The article: every string resolved (spintax), naturalised (no long
  // keyword phrase) and stripped of sales CTAs.
  const a = s.article || {};
  const sec = (v) => ({ ...(v || {}), heading: text(v?.heading), paragraphs: list(v?.paragraphs) });
  const article = {
    intro: list(a.intro),
    what_it_is: sec(a.what_it_is),
    how_it_works: { ...sec(a.how_it_works), steps: list(a.how_it_works?.steps) },
    features: {
      heading: text(a.features?.heading),
      lead: text(a.features?.lead),
      items: (a.features?.items || []).map((item) => ({ heading: text(item.heading), paragraphs: list(item.paragraphs) })),
    },
    user_feedback: sec(a.user_feedback),
    pricing: sec(a.pricing),
    pros_cons: { heading: text(a.pros_cons?.heading), pros: list(a.pros_cons?.pros), cons: list(a.pros_cons?.cons) },
    who_for: { ...sec(a.who_for), good_fit: list(a.who_for?.good_fit), poor_fit: list(a.who_for?.poor_fit) },
    alternatives: sec(a.alternatives),
    before_you_buy: { heading: text(a.before_you_buy?.heading), items: list(a.before_you_buy?.items) },
    verdict: sec(a.verdict),
    // 3-5 of the FAQs per page, in a varied order.
    faq: {
      heading: text(a.faq?.heading),
      items: pickFaq(a.faq?.items || [], rng).map((item) => ({ q: text(item.q), a: text(item.a) })).filter((item) => item.q && item.a),
    },
    evidenceCommentary: text(s.evidence_commentary),
  };
  // The intro must open with the answer; a filler first sentence is dropped.
  // "YouTube video description" style: the hook + summary become the intro,
  // and the rest is rendered as a short, scannable description.
  if (s.description) {
    const d = s.description;
    article.description = {
      experience: list(d.experience),
      takeaways: list(d.takeaways),
      pros: list(d.pros),
      cons: list(d.cons),
      pricing: text(d.pricing),
      whoFor: text(d.who_for),
      whoShouldSkip: text(d.who_should_skip),
      userFeedback: text(d.user_feedback),
      verdict: text(d.verdict),
      faq: (d.faq || []).map((item) => ({ q: text(item.q), a: text(item.a) })).filter((item) => item.q && item.a),
      hashtags: (d.hashtags || []).slice(0, 5),
    };
    article.intro = [text(d.hook), ...list(d.summary)].filter(Boolean);
  }
  const verdictText = article.description?.verdict || article.verdict.paragraphs[0] || '';
  if (article.intro.length) article.intro[0] = answerFirstIntro(article.intro[0], verdictText);
  const introText = article.intro.join(' ');
  const standfirst = text(s.standfirst) || (article.intro[0] || '').split(/(?<=[.!?])\s+/)[0];

  const secondaryVideoSubtitles = list(s.secondary_video_subtitles);
  const secondaryChapterSets = Array.isArray(s.secondary_chapter_sets)
    ? s.secondary_chapter_sets.map((chapSet) => list(chapSet))
    : [[], [], [], []];

  // Every title on the page is the user's SEO title exactly as entered,
  // plus the random code. No LLM rewriting, geo-stripping or truncation.
  const seoTitle = pageTitle || keyword;

  return {
    seo_title: seoTitle,
    subtitle,
    trailing,
    meta_description: text(s.meta_description),
    meta_keywords: `${product} review, ${product} features, ${product} limitations`,
    og_description_head: text(s.og_description),
    twitter_description_head: text(s.twitter_description),
    // A breadcrumb is a short label, not a paragraph.
    breadcrumb_blurb: `${product} Review`,
    standfirst,
    video_title_date_product: product,
    main_video_description: introText,
    card_titles: cards.titles,
    card_contents: cards.contents,
    json_ld_description: text(s.json_ld_description),
    hyd_og_title: seoTitle,
    hyd_og_description: text(s.hyd_og_description),
    hyd_twitter_title: seoTitle,
    hyd_twitter_description: text(s.hyd_twitter_description),
    hyd_meta_title: seoTitle,
    hyd_title_element: seoTitle,
    secondary_video_subtitles: secondaryVideoSubtitles,
    secondary_chapter_sets: secondaryChapterSets,
    article,
  };
};